<template>
  <div>
    <div class="py-3"></div>
    <v-card class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>
      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>하우스 애드 · 앱관리</v-toolbar-title>
        <v-spacer />
        <v-btn color="white" outlined @click="openRegistDialog" :disabled="loading">
          <v-icon left>mdi-plus</v-icon>
          앱 등록
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-data-table
          v-if="housead_app_list.length > 0"
          :headers="headers"
          :items="housead_app_list"
          :items-per-page="15"
          class="elevation-0"
          hide-default-footer
        >
          <template v-slot:item.thumbnail="{ item }">
            <v-avatar size="48" rounded>
              <img
                v-if="item.thumbnail"
                :src="thumbUrl(item.thumbnail)"
                alt="썸네일"
                style="object-fit: cover;"
              />
              <v-icon v-else color="grey">mdi-image-off</v-icon>
            </v-avatar>
          </template>
          <template v-slot:item.appName="{ item }">
            <span class="font-weight-medium">{{ item.appName || '-' }}</span>
          </template>
          <template v-slot:item.android="{ item }">
            <div class="text-body-2 d-flex align-center flex-wrap">
              <span class="mr-2">{{ item.android.packageName || '-' }}</span>
              <v-chip x-small :color="item.android.active === 'Y' ? 'success' : 'grey'" dark>
                {{ item.android.active === 'Y' ? '활성' : '비활성' }}
              </v-chip>
            </div>
          </template>
          <template v-slot:item.ios="{ item }">
            <div class="text-body-2 d-flex align-center flex-wrap">
              <span class="mr-2">{{ item.ios.urlScheme || '-' }}</span>
              <v-chip x-small :color="item.ios.active === 'Y' ? 'success' : 'grey'" dark>
                {{ item.ios.active === 'Y' ? '활성' : '비활성' }}
              </v-chip>
            </div>
          </template>
          <template v-slot:item.actions="{ item, index }">
            <v-btn icon small @click="openEditDialog(index)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <v-container v-if="housead_app_list.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-cellphone-link</v-icon>
          <p class="text-body-1 grey--text mt-3">등록된 앱이 없습니다. '앱 등록' 버튼으로 추가하세요.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 앱 등록/수정 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="820">
      <v-card>
        <v-card-title>
          {{ editIndex >= 0 ? '앱 수정' : '앱 등록' }}
        </v-card-title>

        <v-tabs v-model="tab" background-color="grey lighten-4">
          <v-tab>앱 정보</v-tab>
          <v-tab :disabled="editIndex < 0">
            서드파티 연동
            <v-tooltip bottom v-if="editIndex < 0">
              <template v-slot:activator="{ on }">
                <v-icon small class="ml-1" v-on="on">mdi-information-outline</v-icon>
              </template>
              <span>앱을 먼저 등록한 뒤 설정할 수 있습니다.</span>
            </v-tooltip>
          </v-tab>
          <v-tab :disabled="editIndex < 0">스토어</v-tab>
          <v-tab :disabled="editIndex < 0">전화번호 인증</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- ───────── 탭1: 앱 정보 ───────── -->
          <v-tab-item>
        <v-card-text>
          <v-text-field
            v-model="form.appName"
            label="앱 이름 (표시용)"
            outlined
            dense
            hide-details
            class="mb-3"
          />
          <div class="mb-3">
            <div class="text-caption mb-1">썸네일 이미지</div>
            <v-img
              v-if="form.thumbnail && !form.thumbnailFile"
              :src="thumbUrl(form.thumbnail)"
              max-height="80"
              max-width="80"
              contain
              class="mb-2"
            />
            <v-file-input
              v-model="form.thumbnailFile"
              label="썸네일 선택"
              accept="image/*"
              prepend-icon="mdi-image"
              dense
              outlined
              hide-details
            />
          </div>
          <div class="mb-3">
            <div class="text-caption mb-1">배너 이미지</div>
            <v-img
              v-if="form.banner && !form.bannerFile"
              :src="thumbUrl(form.banner)"
              max-height="80"
              max-width="160"
              contain
              class="mb-2"
            />
            <v-file-input
              v-model="form.bannerFile"
              label="배너 선택"
              accept="image/*"
              prepend-icon="mdi-image"
              dense
              outlined
              hide-details
            />
          </div>
          <v-divider class="my-3" />
          <v-text-field
            v-model="form.postback_path"
            label="Postback Path"
            outlined
            dense
            hide-details
            class="mb-3"
            placeholder="https://your-server.com/postback"
          />
          <v-divider class="my-3" />
          <div class="text-subtitle-2 mb-2">Android</div>
          <v-text-field
            v-model="form.android.packageName"
            label="패키지명 (고유키)"
            outlined
            dense
            hide-details
            class="mb-2"
            placeholder="com.example.app"
          />
          <v-text-field
            v-model="form.android.landingUrl"
            label="랜딩 URL"
            outlined
            dense
            hide-details
            class="mb-2"
            placeholder="https://..."
          />
          <v-select
            v-model="form.android.active"
            :items="[{ text: '활성화', value: 'Y' }, { text: '비활성화', value: 'N' }]"
            label="활성화"
            outlined
            dense
            hide-details
            class="mb-3"
          />
          <v-divider class="my-3" />
          <div class="text-subtitle-2 mb-2">iOS</div>
          <v-text-field
            v-model="form.ios.urlScheme"
            label="URL 스킴 (고유키)"
            outlined
            dense
            hide-details
            class="mb-2"
            placeholder="myapp://"
          />
          <v-text-field
            v-model="form.ios.landingUrl"
            label="랜딩 URL"
            outlined
            dense
            hide-details
            class="mb-2"
            placeholder="https://..."
          />
          <v-select
            v-model="form.ios.active"
            :items="[{ text: '활성화', value: 'Y' }, { text: '비활성화', value: 'N' }]"
            label="활성화"
            outlined
            dense
            hide-details
          />
        </v-card-text>
          </v-tab-item>

          <!-- ───────── 탭2: 서드파티 연동 (스토어·전화인증 공용 자산) ───────── -->
          <v-tab-item>
            <v-card-text>
              <v-alert type="info" dense text class="mb-4">
                여기 설정은 <strong>스토어와 전화번호 인증에서 함께 사용</strong>하는 공용 자산입니다.
              </v-alert>

              <!-- 자격증명 -->
              <div class="text-subtitle-2 mb-2">
                <v-icon small left>mdi-key-variant</v-icon>자격증명
              </div>
              <v-card outlined class="pa-3 mb-4">
                <v-text-field
                  :value="thirdparty.appKey || '(미발급)'"
                  label="APP_KEY"
                  outlined dense readonly hide-details
                  class="mb-2"
                  append-icon="mdi-content-copy"
                  @click:append="copyText(thirdparty.appKey)"
                />
                <!-- 시크릿은 HMAC 검증에 평문이 필요해 해시 저장이 불가 → 언제든 조회 가능 -->
                <v-text-field
                  :value="secretShown ? thirdparty.secret : (thirdparty.hasSecret ? '••••••••••••••••' : '(미발급)')"
                  label="SECRET"
                  outlined dense readonly hide-details
                  class="mb-2"
                  :append-icon="secretShown ? 'mdi-content-copy' : undefined"
                  @click:append="copyText(thirdparty.secret)"
                />
                <div class="d-flex align-center">
                  <v-btn
                    small text color="primary"
                    :loading="tpLoading"
                    :disabled="!thirdparty.hasSecret"
                    @click="toggleSecret"
                  >
                    <v-icon left small>{{ secretShown ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    {{ secretShown ? '숨기기' : '시크릿 보기' }}
                  </v-btn>
                  <v-spacer />
                  <v-btn small color="warning" outlined :loading="tpLoading" @click="issueCredentials">
                    <v-icon left small>mdi-autorenew</v-icon>
                    {{ thirdparty.appKey ? '재발급' : '발급' }}
                  </v-btn>
                </div>
                <div v-if="thirdparty.appKey" class="text-caption grey--text mt-2">
                  재발급하면 기존 시크릿은 즉시 무효화됩니다.
                </div>
              </v-card>

              <!-- 허용 IP -->
              <div class="text-subtitle-2 mb-2">
                <v-icon small left>mdi-ip-network-outline</v-icon>허용 IP
              </div>
              <v-card outlined class="pa-3">
                <v-alert
                  v-if="(form.coupon.enabled === 'Y' || form.phoneAuth.enabled === 'Y') && activeIpCount === 0"
                  type="error" dense text class="mb-3"
                >
                  활성 IP가 없으면 이 앱의 서드파티 호출은 <strong>전부 차단</strong>됩니다.
                </v-alert>

                <v-simple-table dense v-if="allowIps.length > 0">
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th>메모</th>
                      <th style="width:70px" class="text-center">상태</th>
                      <th style="width:60px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ip in allowIps" :key="ipPidOf(ip) || ip.IP_CIDR || ip.ipCidr">
                      <td><code>{{ ip.IP_CIDR || ip.ipCidr }}</code></td>
                      <td class="text-body-2">{{ ip.MEMO || ip.memo || '-' }}</td>
                      <td class="text-center">
                        <v-chip x-small :color="isIpActive(ip) ? 'success' : 'grey'" dark>
                          {{ isIpActive(ip) ? '활성' : '비활성' }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-btn icon x-small @click="removeAllowIp(ip)">
                          <v-icon small color="error">mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div v-else class="text-center grey--text text-caption py-3">
                  등록된 허용 IP가 없습니다.
                </div>

                <v-row dense class="mt-2">
                  <v-col cols="12" sm="5">
                    <v-text-field
                      v-model.trim="newIp.ipCidr"
                      label="IP (여러 개는 콤마로 구분)"
                      outlined dense hide-details
                      placeholder="52.79.169.205, 52.79.169.204"
                    />
                  </v-col>
                  <v-col cols="12" sm="5">
                    <v-text-field
                      v-model.trim="newIp.memo"
                      label="메모"
                      outlined dense hide-details
                      placeholder="앱서버"
                    />
                  </v-col>
                  <v-col cols="12" sm="2">
                    <v-btn
                      color="primary" depressed block
                      :loading="tpLoading"
                      :disabled="!newIp.ipCidr"
                      @click="addAllowIp"
                    >추가</v-btn>
                  </v-col>
                </v-row>
                <div class="text-caption grey--text mt-2">
                  IP 추가/삭제는 즉시 반영됩니다.
                  콤마·공백·개행으로 <strong>여러 개를 한 번에</strong> 등록할 수 있습니다.
                  <span class="red--text">대역(CIDR, 예: 10.0.0.0/24)은 지원하지 않습니다.</span>
                  하나라도 형식이 틀리면 전부 등록되지 않습니다.
                </div>
              </v-card>
            </v-card-text>
          </v-tab-item>

          <!-- ───────── 탭3: 스토어 (쿠폰 구매 설정) ───────── -->
          <v-tab-item>
            <v-card-text>
              <div class="text-subtitle-2 mb-2">
                <v-icon small left>mdi-ticket-percent</v-icon>쿠폰 구매
              </div>
              <v-card outlined class="pa-3">
                <v-switch
                  v-model="form.coupon.enabled"
                  true-value="Y" false-value="N"
                  label="쿠폰 구매 사용"
                  dense hide-details class="mt-0 mb-2"
                />
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="form.coupon.appDailyLimit"
                      label="앱 1일 한도 (원)"
                      type="number" outlined dense
                      hint="0 = 무제한" persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="form.coupon.userDailyLimit"
                      label="유저 1일 한도 (원)"
                      type="number" outlined dense
                      hint="0 = 무제한" persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="form.coupon.userDailyCount"
                      label="유저 1일 건수 (건)"
                      type="number" outlined dense
                      hint="0 = 무제한" persistent-hint
                    />
                  </v-col>
                </v-row>
                <div class="text-caption grey--text mt-2">
                  한도 값은 하단 [저장] 버튼을 눌러야 반영됩니다.
                  자격증명·허용 IP는 [서드파티 연동] 탭에서 관리합니다.
                </div>
              </v-card>
            </v-card-text>
          </v-tab-item>

          <!-- ───────── 탭4: 전화번호 인증 ───────── -->
          <v-tab-item>
            <v-card-text>
              <div class="text-subtitle-2 mb-2">
                <v-icon small left>mdi-cellphone-check</v-icon>전화번호 인증
              </div>
              <v-card outlined class="pa-3">
                <v-switch
                  v-model="form.phoneAuth.enabled"
                  true-value="Y" false-value="N"
                  label="전화번호 인증 사용"
                  dense hide-details class="mt-0 mb-3"
                />
                <v-text-field
                  v-model.trim="form.phoneAuth.postbackUrl"
                  label="전화번호 인증 포스트백 URL"
                  outlined dense
                  prepend-inner-icon="mdi-webhook"
                  placeholder="https://your-server.com/phone_auth/postback"
                  hint="인증 결과를 수신할 서버 주소입니다. 하단 [저장] 시 반영됩니다."
                  persistent-hint
                />
                <div class="text-caption grey--text mt-3">
                  자격증명(APP_KEY/시크릿)과 허용 IP는 [서드파티 연동] 탭에서 관리합니다.
                  인증 이력과 포스트백 재전송은 좌측 메뉴 [전화번호 인증] 화면에서 확인합니다.
                </div>
              </v-card>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">취소</v-btn>
          <v-btn color="primary" @click="saveApp">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 시크릿 1회 노출 다이얼로그 -->
    <v-dialog v-model="secretDialog" max-width="620" persistent>
      <v-card>
        <v-card-title class="warning white--text">
          <v-icon left dark>mdi-alert</v-icon>시크릿 발급 완료
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" dense text class="mb-3">
            <strong>기존 시크릿은 즉시 무효화</strong>되었습니다. 앱에 새 값을 등록하세요.
            (이 값은 [서드파티 연동] 탭의 <strong>[시크릿 보기]</strong>로 언제든 다시 확인할 수 있습니다.)
          </v-alert>
          <v-text-field
            :value="issuedSecret.appKey"
            label="APP_KEY"
            outlined dense readonly hide-details class="mb-2"
            append-icon="mdi-content-copy"
            @click:append="copyText(issuedSecret.appKey)"
          />
          <v-text-field
            :value="issuedSecret.secret"
            label="SECRET"
            outlined dense readonly hide-details
            append-icon="mdi-content-copy"
            @click:append="copyText(issuedSecret.secret)"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="secretDialog = false">복사했습니다</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 오류는 어느 IP가 틀렸는지 등 내용이 길어 오래 띄운다 -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" top>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { housead } from '../../api';

const { FileUpload_Async } = require('../../util/fileutil');
const S3_BASE = 'https://content-filecenter.s3.ap-northeast-2.amazonaws.com/';

const defaultForm = () => ({
  appName: '',
  thumbnail: '',
  thumbnailFile: null,
  banner: '',
  bannerFile: null,
  postback_path: '',
  android: {
    packageName: '',
    landingUrl: '',
    active: 'Y',
  },
  ios: {
    urlScheme: '',
    landingUrl: '',
    active: 'Y',
  },
  // 스토어 연동(쿠폰 구매) 설정 — 저장 시 updateApp 에 함께 실려 나감
  coupon: {
    enabled: 'N',
    appDailyLimit: 0,
    userDailyLimit: 0,
    userDailyCount: 0,
  },
  // 전화번호 인증 — 연동 구현 전이라 값만 미리 보관
  phoneAuth: {
    enabled: 'N',
    postbackUrl: '',
  },
});

export default {
  name: 'HouseAd_AppList',
  computed: {
    housead_app_list() {
      return this.$store.state.housead_app_list || [];
    },
    // 활성 IP 0건이면 서드파티 호출이 전면 차단되므로 경고 표시에 사용
    activeIpCount() {
      return this.allowIps.filter((ip) => this.isIpActive(ip)).length;
    },
    // 현재 편집중인 앱의 PID (신규 등록 중이면 null)
    editingAppId() {
      if (this.editIndex < 0) return null;
      const item = this.housead_app_list[this.editIndex];
      return item ? item.id : null;
    },
  },
  data: () => ({
    dialog: false,
    editIndex: -1,
    form: defaultForm(),
    loading: false,
    // ── 서드파티 연동 탭 ──
    tab: 0,
    tpLoading: false,
    thirdparty: { appKey: null, hasSecret: false, secret: '' },
    secretShown: false,
    allowIps: [],
    newIp: { ipCidr: '', memo: '' },
    secretDialog: false,
    issuedSecret: { appKey: '', secret: '' },
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    snackbarTimeout: 2500,
    headers: [
      { text: '썸네일', value: 'thumbnail', width: '80', sortable: false },
      { text: '앱 이름', value: 'appName', width: '140' },
      { text: 'Android', value: 'android', sortable: false },
      { text: 'iOS', value: 'ios', sortable: false },
      { text: '액션', value: 'actions', width: '100', sortable: false, align: 'center' },
    ],
  }),
  mounted() {
    this.loadHouseAd();
  },
  methods: {
    loadHouseAd() {
      this.loading = true;
      this.$store.dispatch('HOUSEAD_FETCH_CMS_INIT')
        .catch((err) => {
          alert(err && err.message ? err.message : '하우스 애드 데이터를 불러오지 못했습니다.');
        })
        .finally(() => { this.loading = false; });
    },
    thumbUrl(path) {
      if (!path) return '';
      return path.startsWith('http') ? path : S3_BASE + path;
    },
    createNewImageName(originalFileName) {
      const ext = originalFileName.substring(originalFileName.lastIndexOf('.'));
      const random = Math.random().toString(36).substring(2, 18);
      return random + ext;
    },
    openRegistDialog() {
      this.editIndex = -1;
      this.form = defaultForm();
      this.resetThirdparty();
      this.tab = 0;
      this.dialog = true;
    },
    openEditDialog(index) {
      this.editIndex = index;
      const item = this.housead_app_list[index];
      const c = item.coupon || {};
      const pa = item.phoneAuth || {};
      this.form = {
        appName: item.appName || '',
        thumbnail: item.thumbnail || '',
        thumbnailFile: null,
        banner: item.banner || '',
        bannerFile: null,
        postback_path: item.postback_path || '',
        android: {
          packageName: (item.android && item.android.packageName) || '',
          landingUrl: (item.android && item.android.landingUrl) || '',
          active: (item.android && item.android.active) || 'Y',
        },
        ios: {
          urlScheme: (item.ios && item.ios.urlScheme) || '',
          landingUrl: (item.ios && item.ios.landingUrl) || '',
          active: (item.ios && item.ios.active) || 'Y',
        },
        coupon: {
          enabled: c.enabled === 'Y' ? 'Y' : 'N',
          appDailyLimit: Number(c.appDailyLimit || 0),
          userDailyLimit: Number(c.userDailyLimit || 0),
          userDailyCount: Number(c.userDailyCount || 0),
        },
        phoneAuth: {
          enabled: pa.enabled === 'Y' ? 'Y' : 'N',
          postbackUrl: pa.postbackUrl || '',
        },
      };
      this.resetThirdparty();
      this.thirdparty = { appKey: item.appKey || null, hasSecret: !!item.hasSecret };
      this.tab = 0;
      this.dialog = true;
      this.loadAllowIps();
    },

    // ── 서드파티 연동 ──
    resetThirdparty() {
      this.thirdparty = { appKey: null, hasSecret: false, secret: '' };
      this.secretShown = false;
      this.allowIps = [];
      this.newIp = { ipCidr: '', memo: '' };
    },
    // 시크릿은 HMAC 검증에 평문이 필요해 해시 저장이 불가 → 언제든 조회 가능한 정책.
    // 다만 기본은 가려두고, [보기] 를 눌렀을 때만 서버에서 받아온다.
    toggleSecret() {
      if (this.secretShown) {
        this.secretShown = false;
        return;
      }
      if (this.thirdparty.secret) {
        this.secretShown = true;
        return;
      }
      const pid = this.editingAppId;
      if (!pid) return;
      this.tpLoading = true;
      housead.getAppCredentials(pid)
        .then((res) => {
          const appKey = (res && (res.appKey || res.APP_KEY)) || this.thirdparty.appKey;
          const secret = (res && (res.secret || res.SECRET)) || '';
          this.thirdparty = { appKey, hasSecret: !!secret, secret };
          this.secretShown = true;
        })
        .catch((e) => {
          this.showSnack(typeof e === 'string' ? e : '시크릿 조회에 실패했습니다.', 'error');
        })
        .finally(() => { this.tpLoading = false; });
    },
    showSnack(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || 'success';
      this.snackbarTimeout = color === 'error' ? 8000 : 2500;
      this.snackbar = true;
    },
    // 백엔드가 대문자 스네이크/카멜 중 무엇을 주든 동작하도록 둘 다 허용
    isIpActive(ip) {
      const v = ip.ACTIVE !== undefined ? ip.ACTIVE : ip.active;
      return v === 'Y' || v === true || v === undefined;
    },
    ipPidOf(ip) {
      // 백엔드 get_app_allow_ips 는 PID 를 { id } 로 내려준다 → PID/pid/id 순으로 확인.
      if (ip.PID !== undefined) return ip.PID;
      if (ip.pid !== undefined) return ip.pid;
      return ip.id;
    },
    copyText(text) {
      if (!text) return;
      const done = () => this.showSnack('복사했습니다.', 'success');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          this.showSnack('복사에 실패했습니다. 직접 선택해 복사하세요.', 'error');
        });
        return;
      }
      // clipboard API 미지원(비 HTTPS 등) 폴백
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); done(); } catch (e) {
        this.showSnack('복사에 실패했습니다. 직접 선택해 복사하세요.', 'error');
      }
      document.body.removeChild(el);
    },
    loadAllowIps() {
      const pid = this.editingAppId;
      if (!pid) return;
      this.tpLoading = true;
      housead.getAppAllowIps(pid)
        .then((res) => {
          // 응답이 배열이거나 { allowIps | allow_ips | list } 형태 모두 대응
          const list = Array.isArray(res)
            ? res
            : (res && (res.allowIps || res.allow_ips || res.list)) || [];
          this.allowIps = list;
        })
        .catch((e) => {
          console.error('허용 IP 조회 실패:', e);
          this.allowIps = [];
        })
        .finally(() => { this.tpLoading = false; });
    },
    addAllowIp() {
      const pid = this.editingAppId;
      if (!pid || !this.newIp.ipCidr) return;
      this.tpLoading = true;
      housead.createAppAllowIp(pid, {
        ipCidr: this.newIp.ipCidr,
        memo: this.newIp.memo || '',
        active: 'Y',
      })
        .then(() => {
          this.newIp = { ipCidr: '', memo: '' };
          this.showSnack('허용 IP를 추가했습니다.', 'success');
          this.loadAllowIps();
        })
        .catch((e) => {
          this.showSnack(typeof e === 'string' ? e : 'IP 추가에 실패했습니다.', 'error');
        })
        .finally(() => { this.tpLoading = false; });
    },
    removeAllowIp(ip) {
      const pid = this.editingAppId;
      const ipPid = this.ipPidOf(ip);
      if (!pid || ipPid === undefined) return;
      if (!confirm('이 허용 IP를 삭제하시겠습니까?')) return;
      this.tpLoading = true;
      housead.deleteAppAllowIp(pid, ipPid)
        .then(() => {
          this.showSnack('허용 IP를 삭제했습니다.', 'success');
          this.loadAllowIps();
        })
        .catch((e) => {
          this.showSnack(typeof e === 'string' ? e : 'IP 삭제에 실패했습니다.', 'error');
        })
        .finally(() => { this.tpLoading = false; });
    },
    issueCredentials() {
      const pid = this.editingAppId;
      if (!pid) return;
      const msg = this.thirdparty.appKey
        ? '자격증명을 재발급하면 기존 시크릿은 즉시 무효화됩니다. 계속하시겠습니까?'
        : '이 앱의 자격증명을 발급하시겠습니까?';
      if (!confirm(msg)) return;
      this.tpLoading = true;
      housead.issueAppCredentials(pid)
        .then((res) => {
          const appKey = (res && (res.appKey || res.APP_KEY)) || '';
          const secret = (res && (res.secret || res.SECRET)) || '';
          this.thirdparty = { appKey, hasSecret: true, secret };
          // 시크릿은 이후에도 조회 가능하지만, 발급 직후 바로 복사할 수 있게 한 번 띄워준다
          this.issuedSecret = { appKey, secret };
          this.secretDialog = true;
        })
        .catch((e) => {
          this.showSnack(typeof e === 'string' ? e : '자격증명 발급에 실패했습니다.', 'error');
        })
        .finally(() => { this.tpLoading = false; });
    },
    async saveApp() {
      if (!this.form.appName || !this.form.appName.trim()) {
        alert('앱 이름을 입력해주세요.');
        return;
      }
      let thumbnail = this.form.thumbnail;
      if (this.form.thumbnailFile) {
        const path = `housead/apps/${this.createNewImageName(this.form.thumbnailFile.name)}`;
        await FileUpload_Async(this.form.thumbnailFile, path);
        thumbnail = path;
      }
      let banner = this.form.banner;
      if (this.form.bannerFile) {
        const path = `housead/apps/banner_${this.createNewImageName(this.form.bannerFile.name)}`;
        await FileUpload_Async(this.form.bannerFile, path);
        banner = path;
      }
      const payload = {
        appName: this.form.appName.trim(),
        thumbnail,
        banner,
        postback_path: (this.form.postback_path || '').trim(),
        android: { ...this.form.android },
        ios: { ...this.form.ios },
      };
      // 쿠폰/전화번호 설정은 기존 앱 수정일 때만 전송(신규 등록 시엔 두 탭이 비활성)
      if (this.editIndex >= 0) {
        payload.coupon = {
          enabled: this.form.coupon.enabled === 'Y' ? 'Y' : 'N',
          appDailyLimit: Number(this.form.coupon.appDailyLimit || 0),
          userDailyLimit: Number(this.form.coupon.userDailyLimit || 0),
          userDailyCount: Number(this.form.coupon.userDailyCount || 0),
        };
        payload.phoneAuth = {
          enabled: this.form.phoneAuth.enabled === 'Y' ? 'Y' : 'N',
          postbackUrl: (this.form.phoneAuth.postbackUrl || '').trim(),
        };
      }
      const isEdit = this.editIndex >= 0;
      if (isEdit) payload.id = this.housead_app_list[this.editIndex].id;
      this.loading = true;
      const action = isEdit ? 'HOUSEAD_UPDATE_APP' : 'HOUSEAD_CREATE_APP';
      const payloadArg = isEdit ? { index: this.editIndex, item: payload } : { item: payload };
      this.$store.dispatch(action, payloadArg)
        .then(() => { this.dialog = false; })
        .catch((err) => { alert(err && err.message ? err.message : '저장에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    removeApp(index) {
      if (!confirm('이 앱을 삭제하시겠습니까?')) return;
      this.loading = true;
      this.$store.dispatch('HOUSEAD_DELETE_APP', { index })
        .catch((err) => { alert(err && err.message ? err.message : '삭제에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>
